import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  getInstrumentOfSubscale,
  getItem,
  getSubscaleOfItem,
} from '@/lib/schema/loader';
import { useObservation } from '@/lib/state/useObservation';

/**
 * Top ribbon matching the wireframe: prev subscale arrow + label · current
 * "Sx: ITEM NAME" · next subscale label + arrow.
 *
 * Arrows navigate to the FIRST item of the previous / next subscale — matches
 * the wireframe's "‹ Language Reasoning  |  Activities: MATH/NUMBER  |  Interaction ›".
 */
export function ItemRibbon({ itemId }: { itemId: string }) {
  const { state } = useObservation();
  const item = getItem(itemId);
  const subscale = item ? getSubscaleOfItem(item.id) : undefined;
  const instrument = subscale ? getInstrumentOfSubscale(subscale.id) : undefined;

  if (!item || !subscale || !instrument) return null;

  const idx = instrument.subscales.findIndex((s) => s.id === subscale.id);
  const prevSubscale = instrument.subscales[idx - 1];
  const nextSubscale = instrument.subscales[idx + 1];

  const linkTo = (firstItemId: string) =>
    `/observation/${state.id}/items/${firstItemId}`;

  return (
    <div className="flex items-center justify-between gap-4 border-b bg-background px-6 py-3">
      <div className="flex-1">
        {prevSubscale ? (
          <Button
            asChild
            variant="ghost"
            className="group gap-2 text-dece-navy"
          >
            <Link to={linkTo(prevSubscale.items[0]!.id)}>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-dece-navy text-white transition group-hover:bg-dece-navy/90">
                <ChevronLeft className="h-4 w-4" />
              </span>
              <span className="text-sm font-medium">{prevSubscale.name}</span>
            </Link>
          </Button>
        ) : (
          <span className="inline-block h-8 w-8" />
        )}
      </div>
      <div className="text-center">
        <div className="text-xs font-semibold uppercase tracking-wide text-dece-sky">
          {subscale.name}:
        </div>
        <div className="font-serif text-xl font-bold uppercase text-dece-navy">
          {item.name}
        </div>
      </div>
      <div className="flex flex-1 justify-end">
        {nextSubscale ? (
          <Button
            asChild
            variant="ghost"
            className="group gap-2 text-dece-navy"
          >
            <Link to={linkTo(nextSubscale.items[0]!.id)}>
              <span className="text-sm font-medium">{nextSubscale.name}</span>
              <span className="flex h-8 w-8 items-center justify-center rounded-full bg-dece-navy text-white transition group-hover:bg-dece-navy/90">
                <ChevronRight className="h-4 w-4" />
              </span>
            </Link>
          </Button>
        ) : (
          <span className="inline-block h-8 w-8" />
        )}
      </div>
    </div>
  );
}
